import * as yup from "yup";
import { assert } from "@/common/assert";

export type FormValues = Record<string, unknown>;

type OnValidSubmit = (values: FormValues, formElement: HTMLFormElement) => void;

export interface EshopFormValidationOptions {
    validateOnInput?: boolean;
    onValidSubmit?: OnValidSubmit;
    contex?: Record<string, unknown>;
}

export class ValidatedForm<TValues extends FormValues> {
    private readonly form: HTMLFormElement;
    private readonly schema: yup.ObjectSchema<TValues>;
    private readonly options: Required<EshopFormValidationOptions>;
    private readonly handleSubmitBound: (event: SubmitEvent) => Promise<void>;
    private hasSubmitted = false;

    constructor(
        form: HTMLFormElement,
        schema: yup.ObjectSchema<TValues>,
        options: EshopFormValidationOptions = {},
    ) {
        this.form = form;
        this.schema = schema;
        this.options = {
            validateOnInput: options.validateOnInput ?? true,
            onValidSubmit: options.onValidSubmit ?? undefined as unknown as OnValidSubmit,
            contex: options.contex ?? {},
        };

        this.handleSubmitBound = this.handleSubmit.bind(this);
        this.form.addEventListener("submit", (event: SubmitEvent) => void this.handleSubmitBound(event));

        if (this.options.validateOnInput) {
            this.attachPerFieldValidation();
        }
    }

    private collectValues(): TValues {
        const fd = new FormData(this.form);
        const object: FormValues = {};

        for (const [k, v] of fd.entries()) {
            if (typeof v === "string") {
                object[k] = v;
            } else if (v instanceof File) {
                object[k] = v.name;
            }
        }

        return object as TValues;
    }

    private findGroup(element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): HTMLElement | null {
        return element.closest<HTMLDivElement>(".form-group");
    }

    private showFieldError(input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | RadioNodeList, message: string): void {
        const inputElement = input instanceof RadioNodeList ? input[0] : input;
        const group = this.findGroup(inputElement) ?? inputElement.parentElement;

        assert(group !== null);
        inputElement.classList.add("error");
        inputElement.setAttribute("aria-invalid", "true");

        let errorLabel
            = group.querySelector<HTMLLabelElement>(`label.error[for="${inputElement.id}"]`)
              ?? group.querySelector<HTMLLabelElement>(`#${this.createErrorLabelId(inputElement)}`);

        if (!errorLabel) {
            errorLabel = document.createElement("label");
            errorLabel.className = "error";
            errorLabel.id = this.createErrorLabelId(inputElement);
            errorLabel.setAttribute("for", inputElement.id || inputElement.name);
            inputElement.after(errorLabel);
        }

        errorLabel.textContent = message;
    }

    private clearFieldError(input: HTMLInputElement | HTMLTextAreaElement): void {
        if (input instanceof RadioNodeList) {
            const firstElement = input[0] as HTMLInputElement | undefined;

            assert(firstElement !== undefined);

            input = firstElement;
        }

        const group = this.findGroup(input) ?? input.parentElement;

        assert(group !== null);
        input.classList.remove("error");
        input.removeAttribute("aria-invalid");

        const errorLabelFor = group.querySelector<HTMLLabelElement>(
            `label.error[for="${input.id}"]`,
        );
        const errorLabelById = group.querySelector<HTMLLabelElement>(`#${this.createErrorLabelId(input)}`);

        (errorLabelFor ?? errorLabelById)?.remove();
    }

    private async validateField(name: string): Promise<void> {
        const input = this.form.elements.namedItem(name) as HTMLInputElement | null;

        if (!input) {
            return;
        }

        const values = this.collectValues();

        try {
            await this.schema.validateAt(name, values, { abortEarly: true, context: this.options.contex });
            this.clearFieldError(input);
        } catch (error) {
            if (error instanceof yup.ValidationError) {
                this.showFieldError(input, error.message);

                return;
            }

            throw error;
        }
    }

    private attachPerFieldValidation(): void {
        const inputs = [...this.form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, textarea, select")];

        for (const input of inputs) {
            const handler = (): Promise<void> => {
                if (this.hasSubmitted) {
                    return this.validateField(input.name);
                }

                return Promise.resolve();
            };

            if (input instanceof HTMLSelectElement) {
                input.addEventListener("change", () => void handler());
            } else {
                input.addEventListener("blur", () => void handler());
                input.addEventListener("input", () => void handler());
            }
        }
    }

    private focusFirstInvalidField(fieldOrder?: string[]): void {
        const order = fieldOrder ?? ["email", "password", "password2"];

        for (const name of order) {
            const element = this.form.elements.namedItem(name) as HTMLElement | null;

            if ((element?.classList.contains("error")) === true) {
                (element as HTMLInputElement).focus();

                return;
            }
        }

        const firstError = this.form.querySelector<HTMLElement>("input.error, textarea.error");
        firstError?.focus();
    }

    private async handleSubmit(event: SubmitEvent): Promise<void> {
        const submitter = event.submitter;
        event.preventDefault();
        this.hasSubmitted = true;

        const inputs = this.form.querySelectorAll<HTMLInputElement>("input, textarea, select");

        for (const index of inputs) {
            this.clearFieldError(index);
        }

        const values = this.collectValues();

        try {
            const valid = await this.schema.validate(
                values,
                { abortEarly: false, context: this.options.contex },
            );

            if (typeof this.options.onValidSubmit === "function") {
                this.options.onValidSubmit(valid, this.form);
            } else {
                if (submitter instanceof HTMLButtonElement && submitter.type === "submit") {
                    const hiddenInput = document.createElement("input");
                    hiddenInput.type = "hidden";
                    hiddenInput.name = submitter.name;
                    hiddenInput.value = submitter.value;
                    this.form.appendChild(hiddenInput);
                }

                this.form.submit();
            }
        } catch (error) {
            if (error instanceof yup.ValidationError) {
                this.showAllFieldErrors(error);

                return;
            }

            throw error;
        }
    }

    private showAllFieldErrors(error: yup.ValidationError): void {
        const seen = new Set<string>();

        for (const subError of error.inner) {
            const path = subError.path;

            if (path === undefined || seen.has(path)) {
                continue;
            }

            seen.add(path);

            const input = this.form.elements.namedItem(path) as HTMLInputElement | null;

            if (input !== null) {
                this.showFieldError(input, subError.message);
            }
        }

        this.focusFirstInvalidField();
    }

    private createErrorLabelId(input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): string {
        let sanitized = input.name.replace(/[^\w.-]+/g, "-");
        sanitized = sanitized.replace(/-+/g, "-");
        sanitized = sanitized.replace(/(^-+|-+$)/g, "");

        if (sanitized.length === 0) {
            sanitized = "field";
        }

        return `${sanitized}-error`;
    }
}

export default new class EshopFormValidationService {
    public init<TValues extends FormValues>(
        form: HTMLFormElement,
        schema: yup.ObjectSchema<TValues>,
        options: EshopFormValidationOptions = {},
    ): ValidatedForm<TValues> {
        return new ValidatedForm<TValues>(form, schema, options);
    }
}();
